/**
 * React Query hooks for the Cockpit Projects Tracker tool.
 *
 * - useProjects               GET /api/v1/projects/projects (poll every 30s)
 * - useProject(slug)          GET /api/v1/projects/projects/{slug}
 * - useCreateProject          POST /api/v1/projects/projects
 * - useUpdateProject(slug)    PUT  /api/v1/projects/projects/{slug}
 * - useBulkUpsertTasks(slug)  POST /api/v1/projects/projects/{slug}/tasks-bulk-upsert
 * - useUpdateTask(slug)       PUT  /api/v1/projects/projects/{slug}/tasks/{ordinal}
 *
 * RBAC helpers: hasProjectsRead, hasProjectsWrite. The viewer group OID is
 * a placeholder until the all-employees group OID is wired up; reads
 * succeed for any signed-in user even with the placeholder.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut } from './api';
import { API_BASE } from './apiBase';

export const PROJECTS_API_BASE = `${API_BASE}/v1/projects`;

export type ProjectStatus =
  | 'draft'
  | 'in_review'
  | 'in_progress'
  | 'blocked'
  | 'done'
  | 'archived';

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done';

export type Stakeholder = {
  id: number;
  project_id: number;
  oid: string | null;
  display_name: string;
  email: string | null;
  role: string;
  created_at: string;
};

export type StakeholderInput = {
  display_name: string;
  role: string;
  email?: string | null;
  oid?: string | null;
};

export type Project = {
  id: number;
  slug: string;
  name: string;
  summary: string;
  owner_oid: string | null;
  department_slug: string | null;
  started_at: string;
  ended_at: string | null;
  status: ProjectStatus;
  approved: number;
  approved_at: string | null;
  sharepoint_folder_url: string | null;
  confluence_user_doc_url: string | null;
  confluence_troubleshoot_url: string | null;
  jira_request_type_id: string | null;
  spec_docx_path: string | null;
  created_at: string;
  updated_at: string;
  stakeholders?: Stakeholder[];
};

export type ProjectListItem = Project & {
  task_count: number;
  task_done: number;
  stakeholders: Stakeholder[];
};

export type Task = {
  id: number;
  project_id: number;
  ordinal: number;
  title: string;
  description: string | null;
  status: TaskStatus;
  approved: number;
  notes: string | null;
  fixes: string | null;
  questions: string | null;
  owner_oid: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
};

export type ProjectDetail = {
  project: Project;
  tasks: Task[];
};

type Envelope<T> = { data: T };

export type UseProjectsResult = {
  projects: ProjectListItem[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<Envelope<ProjectListItem[]>, Error>;
};

export function useProjects(): UseProjectsResult {
  const query = useQuery<Envelope<ProjectListItem[]>, Error>({
    queryKey: ['projects', 'list'],
    queryFn: () => apiGet<Envelope<ProjectListItem[]>>(`${PROJECTS_API_BASE}/projects`),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 15_000,
  });
  return {
    projects: query.data?.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

export type UseProjectResult = {
  detail: ProjectDetail | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  query: UseQueryResult<Envelope<ProjectDetail>, Error>;
};

export function useProject(slug: string | undefined): UseProjectResult {
  const query = useQuery<Envelope<ProjectDetail>, Error>({
    queryKey: ['projects', 'detail', slug],
    queryFn: () =>
      apiGet<Envelope<ProjectDetail>>(`${PROJECTS_API_BASE}/projects/${encodeURIComponent(slug!)}`),
    enabled: !!slug,
    staleTime: 10_000,
  });
  return {
    detail: query.data?.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: () => query.refetch(),
    query,
  };
}

export type CreateProjectInput = {
  slug: string;
  name: string;
  summary: string;
  started_at: string;
  sharepoint_folder_url?: string | null;
  owner_oid?: string | null;
  status?: ProjectStatus;
  department_slug?: string | null;
  ended_at?: string | null;
  confluence_user_doc_url?: string | null;
  confluence_troubleshoot_url?: string | null;
  jira_request_type_id?: string | null;
  stakeholders?: StakeholderInput[];
};

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation<Envelope<Project>, Error, CreateProjectInput>({
    mutationFn: (input) => apiPost<Envelope<Project>>(`${PROJECTS_API_BASE}/projects`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
}

export type UpdateProjectInput = Partial<{
  name: string;
  summary: string;
  owner_oid: string | null;
  department_slug: string | null;
  started_at: string;
  ended_at: string | null;
  status: ProjectStatus;
  approved: 0 | 1 | boolean;
  sharepoint_folder_url: string | null;
  confluence_user_doc_url: string | null;
  confluence_troubleshoot_url: string | null;
  jira_request_type_id: string | null;
  spec_docx_path: string | null;
  stakeholders: StakeholderInput[];
}>;

export function useUpdateProject(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<Envelope<Project>, Error, UpdateProjectInput>({
    mutationFn: (input) =>
      apiPut<Envelope<Project>>(
        `${PROJECTS_API_BASE}/projects/${encodeURIComponent(slug ?? '')}`,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
      if (slug) {
        void qc.invalidateQueries({ queryKey: ['projects', 'detail', slug] });
      }
    },
  });
}

export type TaskUpsertItem = {
  ordinal: number;
  title: string;
  description?: string | null;
};

export type BulkUpsertResult = {
  upserted: number;
  removed: number;
  missing_from_body: number[];
};

export function useBulkUpsertTasks(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<Envelope<BulkUpsertResult>, Error, TaskUpsertItem[]>({
    mutationFn: (items) =>
      apiPost<Envelope<BulkUpsertResult>>(
        `${PROJECTS_API_BASE}/projects/${encodeURIComponent(slug ?? '')}/tasks-bulk-upsert`,
        { tasks: items },
      ),
    onSuccess: () => {
      if (slug) {
        void qc.invalidateQueries({ queryKey: ['projects', 'detail', slug] });
      }
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
}

export type UpdateTaskInput = Partial<{
  title: string;
  description: string | null;
  status: TaskStatus;
  approved: 0 | 1 | boolean;
  notes: string | null;
  fixes: string | null;
  questions: string | null;
  owner_oid: string | null;
}>;

export function useUpdateTask(slug: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    Envelope<Task>,
    Error,
    { ordinal: number; input: UpdateTaskInput }
  >({
    mutationFn: ({ ordinal, input }) =>
      apiPut<Envelope<Task>>(
        `${PROJECTS_API_BASE}/projects/${encodeURIComponent(slug ?? '')}/tasks/${ordinal}`,
        input,
      ),
    onSuccess: () => {
      if (slug) {
        void qc.invalidateQueries({ queryKey: ['projects', 'detail', slug] });
      }
      void qc.invalidateQueries({ queryKey: ['projects', 'list'] });
    },
  });
}

// ----- RBAC group OIDs ------------------------------------------------------

// Real Entra group OIDs (created 2026-05-22). Membership at creation time:
//   admin  = Steven Pinto, Cyrus Xiong
//   viewer = Rick Armendariz, Elyse Gerald
export const PROJECTS_VIEWER_GROUP_OID = '52798550-f655-407d-af90-f3a44ebf6052';
export const PROJECTS_ADMIN_GROUP_OID = '6c746060-e69e-48c5-8620-6d312bc363b8';

export function hasProjectsRead(_groups: string[] | undefined): boolean {
  // Spec: any authenticated Cockpit user can read.
  return true;
}

export function hasProjectsWrite(groups: string[] | undefined): boolean {
  if (!groups || groups.length === 0) return false;
  return groups.includes(PROJECTS_ADMIN_GROUP_OID);
}

export function projectProgressPercent(p: { task_count: number; task_done: number }): number {
  if (!p.task_count || p.task_count <= 0) return 0;
  return Math.round((p.task_done / p.task_count) * 100);
}
